import { Component, OnInit, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { ChangeLogsApiService } from '../shared/services/change-logs-api.service';

declare const google: any;

@Component({
  selector: 'app-change-logs-login',
  imports: [CommonModule],
  templateUrl: './change-logs-login.html',
  styleUrl: './change-logs-login.css'
})
export class ChangeLogsLogin implements OnInit {
  @ViewChild('googleBtn', { static: true }) googleBtnRef!: ElementRef<HTMLDivElement>;

  private static readonly GSI_SCRIPT_ID = 'change-logs-gsi-script';

  loading = false;
  errorMessage = '';

  constructor(private api: ChangeLogsApiService, private router: Router, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.api.isLoggedIn) {
      this.router.navigate(['/change-logs']);
      return;
    }
    this.loadGoogleScript();
  }

  private loadGoogleScript(): void {
    const existing = document.getElementById(ChangeLogsLogin.GSI_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if ((window as any).google?.accounts?.id) {
        this.initGoogle();
      } else {
        existing.addEventListener('load', () => this.initGoogle());
      }
      return;
    }
    const script = document.createElement('script');
    script.id = ChangeLogsLogin.GSI_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => this.initGoogle();
    script.onerror = () => {
      this.errorMessage = 'Failed to load Google Sign-In. Check your connection and try again.';
      this.cdr.detectChanges();
    };
    document.head.appendChild(script);
  }

  private initGoogle(): void {
    try {
      const clientId = (environment as any).changeLogsGoogleClientId;
      if (!clientId) {
        this.errorMessage = 'Google Sign-In is not configured for this environment.';
        this.cdr.detectChanges();
        return;
      }
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: any) => this.handleCredentialResponse(response)
      });
      google.accounts.id.renderButton(this.googleBtnRef.nativeElement, {
        theme: 'filled_black',
        size: 'large',
        shape: 'rectangular',
        text: 'signin_with',
        width: 280
      });
    } catch {
      this.errorMessage = 'Google Sign-In failed to initialize.';
      this.cdr.detectChanges();
    }
  }

  private handleCredentialResponse(response: any): void {
    const idToken = response?.credential;
    if (!idToken) {
      this.errorMessage = 'Google Sign-In did not return a credential.';
      this.cdr.detectChanges();
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.cdr.detectChanges();
    this.api.login(idToken).subscribe({
      next: (res) => {
        this.loading = false;
        this.api.setSession(res.token, { email: res.email, name: res.name, picture: res.picture });
        this.router.navigate(['/change-logs']);
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 403) {
          this.errorMessage = 'Your Google account is not authorized for OLS Change Logs.';
        } else if (err?.status === 401) {
          this.errorMessage = 'Sign-in failed. Please try again.';
        } else {
          this.errorMessage = 'Something went wrong signing in. Please try again.';
        }
        this.cdr.detectChanges();
      }
    });
  }
}
